/**
 * Orchestration Configuration System
 *
 * This file defines the configuration for different orchestration types
 * to enable unified rendering while preserving all existing functionality.
 */

/**
 * Workflow step definitions for progress calculation and display
 */
export const WORKFLOW_STEP_DEFINITIONS = {
  'fast-track': {
    name: 'Fast Track Campaign',
    description: 'Full workflow with creative processing',
    totalSteps: 4,
    activeSteps: 4,
    stepMapping: {
      'campaign-processing': { order: 1, label: 'Processing IO', progressStart: 0, progressEnd: 33, parallel: true },
      'creative-processing': { order: 2, label: 'Processing Creatives', progressStart: 0, progressEnd: 33, parallel: true },
      'launcher-processing': { order: 3, label: 'Applying Configuration', progressStart: 33, progressEnd: 67 },
      'api-processing': { order: 4, label: 'Calling Channel API', progressStart: 67, progressEnd: 100 }
    },
    parallelSteps: ['campaign-processing', 'creative-processing']
  },
  'fast-track-shell': {
    name: 'Fast Track Campaign Shell',
    description: 'Structure-only workflow without creative processing',
    totalSteps: 4,
    activeSteps: 3, // Creative is skipped
    stepMapping: {
      'campaign-processing': { order: 1, label: 'Processing IO', progressStart: 0, progressEnd: 25 },
      'creative-processing': { order: 2, label: 'Processing Creatives', progressStart: 25, progressEnd: 25, skipped: true },
      'launcher-processing': { order: 3, label: 'Applying Configuration', progressStart: 25, progressEnd: 50 },
      'api-processing': { order: 4, label: 'Calling Channel API', progressStart: 50, progressEnd: 75 }
    },
    parallelSteps: ['campaign-processing'] // Only campaign runs in parallel phase
  }
};

export const ORCHESTRATION_CONFIGS = {
  'fast-track': {
    displayName: 'Fast Track',
    statusField: 'fastTrackStatus',
    steps: ['campaign-processing', 'creative-processing', 'launcher-processing', 'api-processing'],
    stepLabels: {
      'campaign-processing': 'Processing IO',
      'creative-processing': 'Processing Creatives',
      'launcher-processing': 'Applying Configuration',
      'api-processing': 'Calling Channel API'
    },
    // Flow chart uses different step names and labels than progress bar
    flowChartSteps: ['campaign', 'creative', 'launcher', 'api'],
    flowChartLabels: {
      'campaign': 'Insertion Order',
      'creative': 'Creatives',
      'launcher': 'Launcher',
      'api': 'Channel API'
    },
    flowChartHeight: 250,
    hasStopButton: true,
    stopEndpoint: 'stop-fast-track',
    stopHandler: 'handleStopFastTrack',
    collapseStateKey: 'collapsedFastTrack',
    localStorageKey: 'collapsed_fast_track',
    cssClass: 'fast-track',
    messages: {
      initializing: 'Fast Track: Initializing...',
      completed: 'Your campaign has been fast tracked and successfully created on the platform:',
      stopped: 'Fast track process was stopped by user.',
      failed: 'Fast track failed: {error}',
      inProgress: 'Fast Track: {step}',
      polling: 'Fast track in progress: {step} ({percentage}%)',
      pollingComplete: 'Your campaign has been fast tracked and successfully created on the platform:',
      pollingFailed: 'Fast track failed: {error}',
      pollingStopped: 'Fast track process was stopped by user.'
    },
    buttons: {
      stop: 'Stop Fast Track',
      collapse: {
        expand: 'Expand',
        collapse: 'Collapse'
      }
    },
    icons: {
      collapse: 'material-symbols-outlined',
      expandMore: 'expand_more',
      expandLess: 'expand_less'
    }
  },
  'fast-track-shell': {
    displayName: 'Fast Track Campaign Shell',
    statusField: 'fastTrackStatus',
    steps: ['campaign-processing', 'creative-processing', 'launcher-processing', 'api-processing'],
    stepLabels: {
      'campaign-processing': 'Processing IO',
      'creative-processing': 'Processing Creatives',
      'launcher-processing': 'Applying Configuration',
      'api-processing': 'Calling Channel API'
    },
    // Flow chart uses different step names and labels than progress bar
    flowChartSteps: ['campaign', 'creative', 'launcher', 'api'],
    flowChartLabels: {
      'campaign': 'Insertion Order',
      'creative': 'Creatives (Skipped)',
      'launcher': 'Launcher',
      'api': 'Channel API'
    },
    flowChartHeight: 250,
    hasStopButton: true,
    stopEndpoint: 'stop-fast-track',
    stopHandler: 'handleStopFastTrack',
    collapseStateKey: 'collapsedFastTrackShell',
    localStorageKey: 'collapsed_fast_track_shell',
    cssClass: 'fast-track-shell',
    messages: {
      initializing: 'Fast Track Campaign Shell: Initializing...',
      processing: 'Fast Track Campaign Shell: Processing...',
      completed: 'Your campaign shell has been successfully created! Creative processing was skipped as requested.',
      failed: 'Fast Track Campaign Shell: Failed',
      stopped: 'Fast Track Campaign Shell: Stopped',
      inProgress: 'Fast Track Campaign Shell: {step}',
      polling: 'Fast track campaign shell in progress: {step} ({percentage}%)',
      pollingComplete: 'Your campaign shell has been fast tracked and successfully created on the platform! Note: Creative processing was skipped - campaigns were created with structure only.',
      pollingFailed: 'Fast track campaign shell failed: {error}',
      pollingStopped: 'Fast track campaign shell process was stopped by user.'
    },
    buttons: {
      stop: 'Stop Fast Track Shell',
      collapse: {
        expand: 'Expand',
        collapse: 'Collapse'
      }
    },
    icons: {
      collapse: 'material-symbols-outlined',
      expandMore: 'expand_more',
      expandLess: 'expand_less'
    }
  },
  'salesforce-orchestration': {
    displayName: 'Salesforce Orchestration',
    statusField: 'salesforceOrchestrationStatus',
    steps: ['campaign', 'launcher', 'salesforce'],
    stepLabels: {
      'campaign': 'Processing IO',
      'launcher': 'Applying Configuration',
      'salesforce': 'Creating Opportunity'
    },
    // Flow chart uses different step names and labels than progress bar
    flowChartSteps: ['campaign', 'launcher', 'salesforce'],
    flowChartLabels: {
      'campaign': 'Insertion Order',
      'launcher': 'Launcher',
      'salesforce': 'Salesforce API'
    },
    flowChartHeight: 200,
    hasStopButton: true,
    stopEndpoint: 'stop-salesforce-orchestration',
    stopHandler: 'handleStopSalesforceOrchestration',
    collapseStateKey: 'collapsedSalesforceOrchestration',
    localStorageKey: 'collapsed_salesforce_orchestration',
    cssClass: 'salesforce-orchestration',
    messages: {
      initializing: 'Salesforce Orchestration: Initializing...',
      completed: 'Salesforce opportunity has been successfully created!',
      stopped: 'Salesforce orchestration process was stopped by user.',
      failed: 'Salesforce orchestration failed: {error}',
      inProgress: 'Salesforce Orchestration: {step}',
      polling: 'Salesforce orchestration in progress: {step} ({percentage}%)',
      pollingComplete: 'Salesforce opportunity has been successfully created!',
      pollingFailed: 'Salesforce orchestration failed: {error}',
      pollingStopped: 'Salesforce orchestration process was stopped by user.'
    },
    buttons: {
      stop: 'Stop Salesforce Orchestration',
      collapse: {
        expand: 'Expand',
        collapse: 'Collapse'
      }
    },
    icons: {
      collapse: 'material-symbols-outlined',
      expandMore: 'expand_more',
      expandLess: 'expand_less'
    }
  }
};

/**
 * Get orchestration configuration for a given action
 * @param {string} action - The action type ('fast-track' or 'salesforce-orchestration')
 * @returns {Object|null} - The configuration object or null if not found
 */
export const getOrchestrationConfig = (action) => {
  return ORCHESTRATION_CONFIGS[action] || null;
};

/**
 * Get workflow step definition for a given workflow type
 * @param {string} workflowType - The workflow type ('fast-track' or 'fast-track-shell')
 * @returns {Object|null} - The workflow definition or null if not found
 */
export const getWorkflowDefinition = (workflowType) => {
  return WORKFLOW_STEP_DEFINITIONS[workflowType] || null;
};

/**
 * Derive workflow type from message or orchestration data
 * @param {Object} messageOrOrchestration - The message or orchestration data
 * @returns {string} - The workflow type ('fast-track' or 'fast-track-shell')
 */
export const deriveWorkflowType = (messageOrOrchestration) => {
  // If it's a message object with action, use that
  if (messageOrOrchestration.action) {
    return messageOrOrchestration.action === 'fast-track-shell' ? 'fast-track-shell' : 'fast-track';
  }

  // If it's orchestration data, check for metadata first
  if (messageOrOrchestration.metadata) {
    try {
      const metadata = typeof messageOrOrchestration.metadata === 'string'
        ? JSON.parse(messageOrOrchestration.metadata)
        : messageOrOrchestration.metadata;

      if (metadata.workflowType) {
        return metadata.workflowType;
      }
    } catch (error) {
      console.warn('Failed to parse orchestration metadata:', error);
    }
  }

  // Fallback to creative file presence detection
  return messageOrOrchestration.creative_file_path ? 'fast-track' : 'fast-track-shell';
};

/**
 * Get workflow metadata from message or orchestration data
 * @param {Object} messageOrOrchestration - The message or orchestration data
 * @returns {Object} - The workflow metadata
 */
export const getWorkflowMetadata = (messageOrOrchestration) => {
  const workflowType = deriveWorkflowType(messageOrOrchestration);
  const workflow = getWorkflowDefinition(workflowType);

  if (!workflow) {
    return {
      workflowType: 'fast-track',
      isShellWorkflow: false,
      hasCreativeFile: true,
      totalSteps: 4,
      activeSteps: 4,
      parallelSteps: ['campaign-processing', 'creative-processing'],
      workflowName: 'Fast Track Campaign',
      workflowDescription: 'Full workflow with creative processing'
    };
  }

  return {
    workflowType,
    isShellWorkflow: workflowType === 'fast-track-shell',
    hasCreativeFile: workflowType === 'fast-track',
    totalSteps: workflow.totalSteps,
    activeSteps: workflow.activeSteps,
    parallelSteps: workflow.parallelSteps,
    workflowName: workflow.name,
    workflowDescription: workflow.description
  };
};

/**
 * Calculate expected progress percentage for a given step and status
 * @param {string} workflowType - The workflow type
 * @param {string} stepName - The step name
 * @param {string} status - The step status
 * @returns {number} - The expected progress percentage
 */
export const calculateExpectedProgress = (workflowType, stepName, status) => {
  const workflow = getWorkflowDefinition(workflowType);
  if (!workflow || !workflow.stepMapping[stepName]) return 0;

  const step = workflow.stepMapping[stepName];

  let progress;
  switch (status) {
    case 'processing':
      progress = step.progressStart + ((step.progressEnd - step.progressStart) * 0.1); // 10% into current step
      break;
    case 'completed':
    case 'skipped':
      progress = step.progressEnd;
      break;
    case 'failed':
      progress = step.progressStart; // Stay at start of failed step
      break;
    default:
      progress = step.progressStart;
      break;
  }

  return Math.round(progress);
};

/**
 * Calculate overall orchestration progress based on all step statuses (frontend version)
 * @param {string} workflowType - The workflow type
 * @param {Object} orchestrationData - The orchestration data with step statuses
 * @returns {number} - The overall progress percentage
 */
export const calculateOverallProgress = (workflowType, orchestrationData) => {
  const workflow = getWorkflowDefinition(workflowType);
  if (!workflow) return 0;

  const {
    campaign_status = 'pending',
    creative_status = 'pending',
    launcher_status = 'pending',
    api_status = 'pending'
  } = orchestrationData;

  // For fast-track-shell, creative is immediately skipped
  const effectiveCreativeStatus = workflowType === 'fast-track-shell' ? 'skipped' : creative_status;

  // Calculate progress for each step
  const campaignProgress = calculateExpectedProgress(workflowType, 'campaign-processing', campaign_status);
  const creativeProgress = calculateExpectedProgress(workflowType, 'creative-processing', effectiveCreativeStatus);
  const launcherProgress = calculateExpectedProgress(workflowType, 'launcher-processing', launcher_status);
  const apiProgress = calculateExpectedProgress(workflowType, 'api-processing', api_status);

  // For parallel steps (campaign and creative), take the maximum progress
  // For sequential steps, use their individual progress
  let overallProgress = 0;

  if (workflowType === 'fast-track') {
    // Both campaign and creative run in parallel, so take max of both for first 33%
    const parallelProgress = Math.max(campaignProgress, creativeProgress);

    // Progress through the workflow stages
    if (api_status === 'completed' || api_status === 'processing') {
      overallProgress = apiProgress;
    } else if (launcher_status === 'completed' || launcher_status === 'processing') {
      overallProgress = launcherProgress;
    } else if (campaign_status === 'completed' && creative_status === 'completed') {
      // Both parallel steps complete, ready for launcher
      overallProgress = 33;
    } else {
      // Still in parallel phase
      overallProgress = parallelProgress;
    }
  } else {
    // fast-track-shell: campaign runs alone, creative is skipped, then launcher, then API
    if (api_status === 'completed' || api_status === 'processing') {
      overallProgress = apiProgress;
    } else if (launcher_status === 'completed' || launcher_status === 'processing') {
      overallProgress = launcherProgress;
    } else if (campaign_status === 'completed') {
      // Campaign complete, creative skipped, ready for launcher
      overallProgress = 25;
    } else {
      // Campaign still processing
      overallProgress = campaignProgress;
    }
  }

  return Math.round(Math.min(100, Math.max(0, overallProgress)));
};

/**
 * Get orchestration status from a message
 * @param {Object} message - The message object
 * @returns {Object|null} - The orchestration status object or null
 */
export const getOrchestrationStatus = (message) => {
  const config = getOrchestrationConfig(message.action);
  if (!config) return null;
  
  return message[config.statusField] || null;
};

/**
 * Get the appropriate message text for an orchestration
 * @param {Object} status - The orchestration status object
 * @param {Object} config - The orchestration configuration
 * @param {Function} getCurrentStepDisplay - Function to get step display name
 * @returns {string} - The message text to display
 */
export const getOrchestrationMessageText = (status, config, getCurrentStepDisplay) => {
  if (!status) {
    return config.messages.initializing;
  }

  switch (status.status) {
    case 'completed':
      return config.messages.completed;
    case 'stopped':
      return config.messages.stopped;
    case 'failed':
      return config.messages.failed.replace('{error}', 
        status.error_message || 'Connection terminated unexpectedly');
    default:
      const stepDisplay = getCurrentStepDisplay ?
        getCurrentStepDisplay(status.current_step || 'initializing', config) :
        config.stepLabels[status.current_step] || status.current_step || 'initializing';
      return config.messages.inProgress.replace('{step}', stepDisplay);
  }
};

/**
 * Check if orchestration should show stop button
 * @param {Object} status - The orchestration status object
 * @returns {boolean} - Whether to show the stop button
 */
export const shouldShowStopButton = (status) => {
  if (!status) return true; // Show for initial state

  // Hide stop button for upload-creatives and assign-creatives steps
  if (status.currentStep === 'upload-creatives' || status.currentStep === 'assign-creatives') {
    return false;
  }

  return status.status !== 'completed' &&
         status.status !== 'stopped' &&
         status.status !== 'failed';
};

/**
 * Check if orchestration should show processing ID
 * @param {Object} status - The orchestration status object
 * @returns {boolean} - Whether to show the processing ID
 */
export const shouldShowProcessingId = (status) => {
  if (!status) return true; // Show for initial state
  
  return status.status !== 'completed' && 
         status.status !== 'stopped' && 
         status.status !== 'failed';
};

/**
 * Get all supported orchestration actions
 * @returns {Array<string>} - Array of supported action types
 */
export const getSupportedOrchestrationActions = () => {
  return Object.keys(ORCHESTRATION_CONFIGS);
};

/**
 * Check if an action is a supported orchestration type
 * @param {string} action - The action to check
 * @returns {boolean} - Whether the action is a supported orchestration type
 */
export const isOrchestrationAction = (action) => {
  return ORCHESTRATION_CONFIGS.hasOwnProperty(action);
};

/**
 * Generate polling message content using configuration
 * @param {string} action - The orchestration action type
 * @param {Object} orchestrationData - The orchestration status data
 * @param {Function} getCurrentStepDisplay - Function to get step display name
 * @returns {string} - The polling message content
 */
export const getPollingMessage = (action, orchestrationData, getCurrentStepDisplay) => {
  const config = getOrchestrationConfig(action);
  if (!config) return '';

  const stepDisplay = getCurrentStepDisplay ?
    getCurrentStepDisplay(orchestrationData.current_step || 'initializing', config) :
    config.stepLabels[orchestrationData.current_step] || orchestrationData.current_step || 'initializing';

  return config.messages.polling
    .replace('{step}', stepDisplay)
    .replace('{percentage}', orchestrationData.progress_percentage || 0);
};

/**
 * Generate completed polling message content using configuration
 * @param {string} action - The orchestration action type
 * @returns {string} - The completed message content
 */
export const getPollingCompleteMessage = (action) => {
  const config = getOrchestrationConfig(action);
  return config ? config.messages.pollingComplete : '';
};

/**
 * Generate failed polling message content using configuration
 * @param {string} action - The orchestration action type
 * @param {string} errorMessage - The error message
 * @returns {string} - The failed message content
 */
export const getPollingFailedMessage = (action, errorMessage) => {
  const config = getOrchestrationConfig(action);
  if (!config) return '';

  return config.messages.pollingFailed.replace('{error}',
    errorMessage || 'An error occurred during processing');
};

/**
 * Generate stopped polling message content using configuration
 * @param {string} action - The orchestration action type
 * @returns {string} - The stopped message content
 */
export const getPollingStoppedMessage = (action) => {
  const config = getOrchestrationConfig(action);
  return config ? config.messages.pollingStopped : '';
};

/**
 * Get workflow-specific contextual message based on current step and status
 * @param {string} action - The orchestration action type
 * @param {Object} orchestrationData - The orchestration status data
 * @returns {string} - Contextual message explaining current state
 */
export const getWorkflowContextualMessage = (action, orchestrationData) => {
  if (!orchestrationData) return '';

  const { current_step, creative_status, progress_percentage } = orchestrationData;

  if (action === 'fast-track-shell') {
    if (current_step === 'creative-skipped' || creative_status === 'skipped') {
      return 'Creative processing was skipped for this campaign shell workflow. Only campaign structure will be created.';
    }
    if (current_step === 'launcher-processing') {
      return 'Creating campaign structure without creative assets...';
    }
    if (current_step === 'completed') {
      return 'Campaign shell created successfully! No creative assets were processed as this is a structure-only workflow.';
    }
  } else if (action === 'fast-track') {
    if (current_step === 'creative-processing') {
      return 'Processing both campaign structure and creative assets...';
    }
    if (current_step === 'launcher-processing') {
      return 'Applying configuration with creative assets included...';
    }
    if (current_step === 'completed') {
      return 'Campaign created successfully with all creative assets integrated!';
    }
  }

  return '';
};

/**
 * Get step-specific user guidance message
 * @param {string} action - The orchestration action type
 * @param {string} currentStep - The current step
 * @returns {string} - User guidance message
 */
export const getStepGuidanceMessage = (action, currentStep) => {
  const stepGuidance = {
    'fast-track': {
      'campaign-processing': 'Extracting campaign details from your insertion order...',
      'creative-processing': 'Processing your creative assets and preparing them for launch...',
      'launcher-processing': 'Configuring campaign with creative assets...',
      'api-processing': 'Creating campaign on the platform with all assets...',
      'completed': 'Your complete campaign with creatives is now live!'
    },
    'fast-track-shell': {
      'campaign-processing': 'Extracting campaign structure from your insertion order...',
      'creative-skipped': 'Creative processing skipped - creating structure-only campaign...',
      'launcher-processing': 'Configuring campaign structure without creative assets...',
      'api-processing': 'Creating campaign shell on the platform...',
      'completed': 'Your campaign shell is now ready! You can add creatives later through the platform.'
    }
  };

  return stepGuidance[action]?.[currentStep] || '';
};

/**
 * Check if an action is a supported orchestration type that should use unified rendering
 * @param {string} action - The action type
 * @returns {boolean} - Whether the action should use unified orchestration rendering
 */
export const isUnifiedOrchestrationAction = (action) => {
  return action === 'fast-track' || action === 'fast-track-shell' || action === 'salesforce-orchestration';
};
